// Location service for managing practice locations
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getEffectiveAccountId } from './authorizationService';

class LocationService {
  constructor() {
    this.collectionName = 'locations';
  }

  // Create a new location
  async createLocation(locationData, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        throw new Error('No account scope set. Please select an account.');
      }

      const locationWithTimestamp = {
        ...locationData,
        accountId: effectiveAccountId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, this.collectionName), locationWithTimestamp);
      
      return { id: docRef.id, ...locationWithTimestamp };
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  }

  // Get all locations for an account
  async getLocations(accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        console.log('No effective account ID, cannot load locations');
        return [];
      }

      const q = query(
        collection(db, this.collectionName),
        where('accountId', '==', effectiveAccountId)
      );

      const querySnapshot = await getDocs(q);
      const locations = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        locations.push({
          id: doc.id,
          ...data
        });
      });

      // Sort by name
      locations.sort((a, b) => a.name.localeCompare(b.name));

      return locations;
    } catch (error) {
      console.error('Error getting locations:', error);
      return [];
    }
  }

  // Update a location
  async updateLocation(locationId, locationData, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        throw new Error('No account scope set. Please select an account.');
      }

      const updateData = {
        ...locationData,
        updatedAt: new Date().toISOString()
      };

      const locationRef = doc(db, this.collectionName, locationId);
      await updateDoc(locationRef, updateData);

      return { id: locationId, ...updateData };
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  }

  // Delete a location
  async deleteLocation(locationId) {
    try {
      const locationRef = doc(db, this.collectionName, locationId);
      await deleteDoc(locationRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  }

  // Get location by ID
  async getLocationById(locationId, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        return null;
      }

      const q = query(
        collection(db, this.collectionName),
        where('accountId', '==', effectiveAccountId),
        where('__name__', '==', locationId)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error getting location by ID:', error);
      return null;
    }
  }

  // Search locations by name or city
  async searchLocations(searchTerm, accountId) {
    try {
      const effectiveAccountId = getEffectiveAccountId() || accountId;
      if (!effectiveAccountId) {
        return [];
      }

      const q = query(
        collection(db, this.collectionName),
        where('accountId', '==', effectiveAccountId)
      );

      const querySnapshot = await getDocs(q);
      const locations = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const searchableText = `${data.name} ${data.city} ${data.state}`.toLowerCase();
        
        if (searchableText.includes(searchTerm.toLowerCase())) {
          locations.push({
            id: doc.id,
            ...data
          });
        }
      });

      return locations;
    } catch (error) {
      console.error('Error searching locations:', error);
      return [];
    }
  }
}

const locationService = new LocationService();
export default locationService;
